import React, { useContext, useState } from 'react';
import { Box } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Actions } from '../../../enums/ActionEnums';
import { AppContext } from '../../../AppContext';
import axios from 'axios';
import FileSaver from 'file-saver';
import EpraDatePicker from '../../Common/Fields/EpraDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import DialogBox from '../../Common/Layout/DialogBox';
import CancelButton from '../../Common/Buttons/CancelButton';
import SaveButton from '../../Common/Buttons/SaveButton';
import { AppConsts } from '../../../enums/AppConsts';
import CheckboxComponent from '../../Common/Fields/CheckboxComponent';
import './FtseDownloadForm.scss';

type IProps = {
  handleClose: () => void;
  isLoading: (isLoading: boolean) => void;
};

type IErrors = {
  downloadDate: string | null;
  downloadDateInvalidDate: string | null;
};

const defaultErrorsObj: IErrors = {
  downloadDate: null,
  downloadDateInvalidDate: null
};

const errorTexts: IErrors = {
  downloadDate: 'Download Date is required',
  downloadDateInvalidDate: 'Invalid Date Format'
};

const minDate = new Date(2000, 0, 1);
const downloadMinDateMessage = `date should be after ${minDate}`;

const FtseDownloadForm: React.FunctionComponent<IProps> = ({ handleClose, isLoading }: IProps) => {
  const [downloadDate, setDownloadDate] = useState<Date | null>(new Date());
  const [formErrors, setFormErrors] = useState(defaultErrorsObj);
  const [downloadLatest, setDownloadLatest] = useState(false);
  const { dispatch } = useContext(AppContext);

  const handleCancel = () => {
    setDownloadDate(null);
    handleClose();
  };

  const getPureDate = (date: Date | null) => {
    const getMonthWithTwoDigit = (date: Date) => {
      return ('0' + (date?.getMonth() + 1)).slice(-2);
    };

    const getDateWithTwoDigit = (date: Date | null) => {
      return ('0' + date?.getDate()).slice(-2);
    };
    const pureDate = `${date?.getFullYear()}-${getMonthWithTwoDigit(
      date as Date
    )}-${getDateWithTwoDigit(date)}`;
    return pureDate;
  };

  const handleDownload = () => {
    dispatch({
      type: Actions.ShowConfirmation,
      payload: {
        text: `'Are you sure you want to download FTSE files ( on your computer ) for ${getPureDate(
          downloadDate
        )} ?'`,
        agreeAction: downloadFiles
      }
    });
  };

  const downloadFiles = async () => {
    const pureDate = getPureDate(downloadDate);
    dispatch({ type: Actions.HideConfirmation });
    isLoading(true);
    axios({
      url: `${'https://crm.epra.com/csvcollect'}/downloadFiles?date=${pureDate}&latest=${downloadLatest}` as string,
      method: 'GET',
      responseType: 'blob',
      headers: { Authorization: `${localStorage.getItem(AppConsts.AccessToken)}` }
    })
      .then((response) => {
        FileSaver.saveAs(response.data, `ftse-${pureDate}`);
        isLoading(false);
      })
      .catch((e) => {
        isLoading(false);
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: e.message
          }
        });
      });
  };

  const handleDownloadDateChange = (value: Date | null) => {
    if (value) {
      if (value < new Date(minDate)) {
        errorTexts.downloadDate = downloadMinDateMessage;
      } else if (errorTexts.downloadDate === downloadMinDateMessage) {
        errorTexts.downloadDate = null;
      }
      setFormErrors({ ...formErrors, downloadDate: null });
    } else {
      setFormErrors({ ...formErrors, downloadDate: errorTexts.downloadDate });
    }
    setDownloadDate(value);
  };
  const handleSubmit = () => {
    const errors = {
      downloadDate: null,
      downloadDateInvalidDate: null
    } as IErrors;
    let hasErrors = false;
    if (!downloadDate) {
      hasErrors = true;
      errors.downloadDate = errorTexts.downloadDate;
    } else {
      if (!Date.parse(downloadDate.toString())) {
        hasErrors = true;
        errors.downloadDate = errorTexts.downloadDateInvalidDate;
      }
    }
    if (hasErrors) {
      setFormErrors(errors);
    } else {
      handleDownload();
      handleClose();
    }
  };

  return (
    <DialogBox
      id="ftse-files-download-form"
      width={375}
      title="Download FTSE Files"
      onClose={handleClose}
    >
      <Box className="ftse-download-container">
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <EpraDatePicker
            id="start-date-picker"
            label="Download Date"
            value={downloadDate}
            onChange={(date) => handleDownloadDateChange(date)}
            minDate={minDate}
            sx={{ width: '100%' }}
          />
        </LocalizationProvider>
        <CheckboxComponent
          key="latest-files"
          id="latest-files"
          label="Get Latest"
          value={downloadLatest}
          onChange={() => setDownloadLatest(!downloadLatest)}
        />
        <Box className="centralize ftse-download-container__actions">
          <CancelButton id="cancel-download-button" onClick={handleCancel} text="Close" />
          <SaveButton id="save-download-button" onClick={handleSubmit} text="Submit" />
        </Box>
      </Box>
    </DialogBox>
  );
};
export default FtseDownloadForm;
